import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { setAssessmentPrefill } from "@/lib/assessmentPrefill";
import {
  ArrowRight, BadgeCheck, Leaf, DollarSign, Calendar,
  TrendingDown, Sparkles, Download, RefreshCw, Banknote,
  ChevronDown, ChevronUp, Info, AlertCircle, Phone, Mail
} from "lucide-react";
import SavingsChart from "./SavingsChart";

const PRODUCT_IMG = "/products/neopower_lifestyle.png";

export default function CalcResults({
  result,
  lead,
  inputs,
  onRunAgain,
  staffMode = false,
  staffData = null,
  emailStatus = null,        // null | "sending" | "sent" | "failed"
  onEmailReport,             // () => void
  crmProspectId = null,      // AeroMax CRM prospect id (public mode)
}) {
  const annual = Math.round(result.annual_saving || 0);
  const monthly = Math.round(result.monthly_saving || 0);
  const fifteenYr = Math.round(result.fifteen_year_saving || 0);
  const breakEvenYear = result.break_even_year;
  const totalReturn = Math.round(result.total_return_after_life || 0);
  const installedCost = result.installed_cost_used ?? result.installed_cost_low;
  const installedLow = result.installed_cost_low;
  const installedHigh = result.installed_cost_high;
  const systemLife = result.system_life_years;
  const finance = result.finance || { selected: false };
  const assumptions = result.assumptions_used;
  const isNotSure = inputs?.current_system === "not_sure";

  // ---------------------------------------------------------------
  // Assessment-form prefill: write current calc context into
  // sessionStorage just before navigating to /free-home-assessment
  // so the form opens populated. The user can still edit any field.
  // We never include this in the URL (avoids leaking PII).
  // ---------------------------------------------------------------
  const handleBookAssessment = () => {
    const ld = lead || {};
    const ip = inputs || {};
    const sd = staffData || {};

    const showers = Number(ip.showers_per_day);
    const usageLevel = Number.isFinite(showers) && showers > 0
      ? showers < 3 ? "Low" : showers <= 6 ? "Medium" : "High"
      : "Unsure";

    const systemMap = {
      electric_storage: "Electric cylinder",
      gas_storage: "Gas storage",
      continuous_gas: "Gas continuous",
      lpg: "LPG",
      solar_assisted: "Solar hot water",
      not_sure: "Unsure",
    };
    const energyMap = {
      electric_storage: "Electricity",
      gas_storage: "Natural gas",
      continuous_gas: "Natural gas",
      lpg: "LPG bottles",
      not_sure: "Unsure",
    };
    const ageMap = {
      under_5: "Less than 5 years",
      "5_10": "5–10 years",
      "10_15": "10+ years",
      "15_plus": "10+ years",
      not_sure: "Unsure",
    };
    const financeMap = { yes: "Yes", no: "No" };

    const isHomeShow =
      staffMode && (sd.source || "").toLowerCase() === "home show";
    const source = isHomeShow ? "Home Show Assessment" : "Website Assessment";
    const homeShowName = isHomeShow
      ? (sd.home_show === "Other" ? sd.home_show_other : sd.home_show) || null
      : null;
    const specialistName = staffMode
      ? (sd.specialist === "Other" ? sd.specialist_other : sd.specialist) ||
        null
      : null;
    const installerName = staffMode
      ? (sd.installer === "Other" ? sd.installer_other : sd.installer) || null
      : null;

    const addr = ld.address_details || {};
    setAssessmentPrefill({
      // Customer details
      name: ld.name || "",
      email: ld.email || "",
      phone: ld.phone || "",
      address: ld.address || "",
      suburb: addr.sublocality || ld.suburb || "",
      city: addr.locality || ld.city || "",
      postal_code: addr.postal_code || ld.postcode || "",
      country: addr.country || "",
      lat: addr.lat ?? null,
      lng: addr.lng ?? null,
      // Home & usage
      household_size: ip.people ? String(ip.people) : "",
      current_system: systemMap[ip.current_system] || "",
      energy_source: energyMap[ip.current_system] || "",
      usage_level: usageLevel,
      system_age: ageMap[ip.system_age] || "",
      // Assessment details
      interested_finance: financeMap[ip.lower_bill_interest] || "Maybe",
      notes: "",
      // Source + carry-over metadata (preserved through to backend
      // internal_lead_data via the assessment form's submit handler).
      source,
      home_show: homeShowName,
      specialist: specialistName,
      installer: installerName,
    });
  };


  return (
    <div data-testid="calc-results" className="space-y-6">
      {/* HEADER */}
      <div className="text-center sm:text-left">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B132B] leading-[1.05]">
          Your Hot Water <span className="text-[#10B981]">Savings Assessment</span>
        </h2>
        <p className="mt-3 text-[#475569] leading-relaxed max-w-2xl">
          Here's how a NeoPower Black Shield heat pump could reduce your running costs, lower emissions and deliver reliable hot water for years to come.
        </p>
        {lead?.name && (
          <p className="mt-2 text-sm text-[#475569]">
            Prepared for <span className="font-semibold text-[#0B132B]">{lead.name}</span>
            {staffMode && staffData && (() => {
              const sp = staffData.specialist === "Other"
                ? (staffData.specialist_other || "").trim()
                : (staffData.specialist || "").trim();
              return sp ? (
                <>
                  {" · "}
                  <span className="text-[#475569]">
                    Prepared by <span className="font-semibold text-[#0B132B]">{sp}</span>
                  </span>
                </>
              ) : null;
            })()}
          </p>
        )}
      </div>

      {isNotSure && (
        <Card className="rounded-2xl p-5 border-amber-200 bg-amber-50 flex items-start gap-3">
          <AlertCircle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900 leading-relaxed">
            We used conservative NZ household assumptions because your current system type was not confirmed. AeroMax can confirm your setup during assessment.
          </p>
        </Card>
      )}

      {/* HERO SAVINGS STATS */}
      <Card className="rounded-3xl p-8 sm:p-10 border-0 bg-gradient-to-br from-[#0B132B] to-[#050914] text-white relative overflow-hidden">
        <div className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#10B981]/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-5">
            <BadgeCheck className="h-5 w-5 text-[#10B981]" />
            <p className="text-xs uppercase tracking-widest font-semibold text-[#10B981]">Your estimated savings</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">Estimated annual saving</p>
              <p className="font-heading text-4xl sm:text-5xl font-bold text-[#10B981] mt-2 num-tab leading-none">${annual.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">Estimated monthly saving</p>
              <p className="font-heading text-4xl sm:text-5xl font-bold text-white mt-2 num-tab leading-none">${monthly.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">Estimated 15-year saving</p>
              <p className="font-heading text-4xl sm:text-5xl font-bold text-white mt-2 num-tab leading-none">${fifteenYr.toLocaleString()}</p>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              { label: "Current system cost (yr)", val: `$${Math.round(result.current_annual_cost || 0).toLocaleString()}`, icon: DollarSign },
              { label: "Heat pump cost (yr)", val: `$${Math.round(result.heat_pump_annual_cost || 0).toLocaleString()}`, icon: TrendingDown },
              { label: "CO₂ saved (yr)", val: `${Math.round(result.co2_saving_kg || 0)} kg`, icon: Leaf },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                <div className="flex items-center gap-2">
                  <s.icon className="h-3.5 w-3.5 text-[#10B981]" />
                  <p className="text-[10px] uppercase tracking-widest text-white/55 font-semibold">{s.label}</p>
                </div>
                <p className="font-heading text-xl sm:text-2xl font-bold text-white mt-1.5 num-tab">{s.val}</p>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* PRODUCT RECOMMENDATION */}
      <Card className="rounded-3xl border-[#E2E8F0] bg-white overflow-hidden">
        <div className="grid md:grid-cols-12">
          <div className="md:col-span-7 p-7 sm:p-9">
            <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-widest uppercase">
              <Sparkles className="h-3 w-3" /> AeroMax Recommended
            </span>
            <h3 className="font-heading mt-3 text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight">
              NeoPower Black Shield <span className="text-[#10B981]">270L</span>
            </h3>
            <p className="mt-3 text-[#475569] leading-relaxed text-sm sm:text-base">
              The premium all-in-one hot water heat pump — one of the most efficient systems available in NZ. Designed for modern homes upgrading from gas, LPG or electric storage.
            </p>
            <div className="mt-5 grid grid-cols-3 gap-2">
              <Tile label="COP" value={result.cop_used?.toFixed(1) || "5.4"} accent />
              <Tile label="Capacity" value="270L" />
              <Tile label="Confidence" value={result.confidence || "High"} />
            </div>
            <p className="mt-4 text-[11px] text-[#475569] leading-relaxed">
              COP 5.4 is a product performance rating — actual real-world performance may vary based on conditions, installation and usage.
            </p>
          </div>
          <div className="md:col-span-5 relative min-h-[260px] md:min-h-0 bg-gradient-to-br from-[#0B132B] to-[#050914]">
            <img src={PRODUCT_IMG} alt="NeoPower Black Shield 270L installed" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/60 via-transparent to-transparent" />
          </div>
        </div>
      </Card>

      {/* ROI / PAYBACK INSIGHT */}
      <Card className="rounded-3xl p-7 sm:p-9 border-[#E2E8F0] bg-white">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-4 w-4 text-[#10B981]" />
          <p className="text-xs uppercase tracking-widest font-semibold text-[#475569]">ROI &amp; Payback Insight</p>
        </div>
        <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0B132B] leading-tight">
          Cumulative savings over your NeoPower system's <span className="text-[#10B981]">15-year</span> life.
        </h3>
        <p className="mt-2 text-sm text-[#475569] leading-relaxed max-w-2xl">
          Where your savings cross the installed-cost line is your estimated break-even point. Everything after that is projected net return.
        </p>

        <div className="mt-6 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-7">
            <SavingsChart yearly={result.yearly_projection} installedCost={installedCost} />
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 lg:grid-cols-1 gap-3">
            <Stat label="Installed cost range" value={`$${installedLow.toLocaleString()}–$${installedHigh.toLocaleString()}`} />
            <Stat label="Break-even year" value={breakEvenYear ? `Year ${breakEvenYear}` : "Beyond 15 years"} accent={!!breakEvenYear} />
            <Stat label="Estimated system life" value={`${systemLife} years`} />
            <Stat label={`Total return after ${systemLife} yrs`} value={`$${totalReturn.toLocaleString()}`} accent />
          </div>
        </div>
        <p className="mt-5 text-[11px] text-[#475569] leading-relaxed">
          Installation pricing is based on a standard or straight-swap installation. Actual installation cost may vary depending on site conditions, electrical requirements, plumbing changes, access and other installation factors.
        </p>
      </Card>

      {/* MONTHLY SAVINGS VS LOAN REPAYMENT */}
      <FinancePanel finance={finance} monthlySaving={monthly} />

      {/* SUSTAINABILITY PANEL */}
      <Card className="rounded-3xl p-7 sm:p-9 border-0 bg-gradient-to-br from-[#10B981] to-[#059669] text-white overflow-hidden relative">
        <div className="absolute -top-20 -right-20 h-[260px] w-[260px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 backdrop-blur shrink-0">
            <Leaf className="h-6 w-6" />
          </span>
          <div>
            <h3 className="font-heading text-xl sm:text-2xl font-bold leading-tight">Be part of the change.</h3>
            <p className="mt-2 text-white/90 leading-relaxed text-sm sm:text-base max-w-2xl">
              By upgrading from an inefficient hot water system to a NeoPower Black Shield heat pump, you are choosing lower emissions, smarter energy use and a cleaner future for your home.
            </p>
          </div>
        </div>
      </Card>

      {/* INTERNAL LEAD DETAILS — staff mode only */}
      {staffMode && staffData && <InternalLeadPanel staffData={staffData} lead={lead} />}

      {/* ASSUMPTIONS USED */}
      <AssumptionsUsed assumptions={assumptions} inputs={inputs} finance={finance} />

      {/* DISCLAIMER */}
      <Card className="rounded-2xl p-6 border-[#E2E8F0] bg-[#F8FAFC]">
        <p className="text-[11px] text-[#475569] leading-relaxed">
          <strong className="text-[#0B132B]">Disclaimer:</strong> These figures are estimates only and are not a guarantee of savings. Actual savings depend on your household usage, tariffs, climate, installation quality, product settings, existing system efficiency and whether gas supply charges can be removed.
        </p>
      </Card>

      {/* CTA ROW */}
      <div className="flex flex-wrap gap-3">
        <Link
          to={crmProspectId ? `/free-home-assessment?prospectId=${encodeURIComponent(crmProspectId)}` : "/free-home-assessment"}
          data-testid="calc-book-cta"
          onClick={handleBookAssessment}
          className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-4 font-semibold transition-all hover:shadow-lg hover:shadow-emerald-500/30"
        >
          Book Free Home Assessment <ArrowRight className="h-4 w-4" />
        </Link>
        <button
          type="button"
          onClick={onEmailReport}
          data-testid="calc-email-estimate"
          disabled={!onEmailReport || emailStatus === "sending" || emailStatus === "sent"}
          className={`inline-flex items-center gap-2 rounded-full border-2 px-7 py-4 font-semibold transition ${
            emailStatus === "sent"
              ? "border-[#10B981]/40 bg-[#10B981]/5 text-[#059669]"
              : "border-[#E2E8F0] text-[#0B132B] hover:border-[#0B132B]"
          }`}
        >
          <Download className="h-4 w-4" />
          {emailStatus === "sending"
            ? "Sending..."
            : emailStatus === "sent"
            ? "Report Emailed ✓"
            : emailStatus === "failed"
            ? "Retry — Email My Estimate"
            : "Email My Estimate"}
        </button>
        <a
          href="tel:0508237662"
          data-testid="calc-talk-cta"
          className="inline-flex items-center gap-2 rounded-full border-2 border-[#E2E8F0] text-[#0B132B] hover:border-[#0B132B] px-7 py-4 font-semibold transition"
        >
          <Phone className="h-4 w-4" /> Talk to AeroMax · 0508 AEROMAX
        </a>
        <a
          href="mailto:sales@aeromaxenergy.co.nz"
          data-testid="calc-email-us-cta"
          className="inline-flex items-center gap-2 rounded-full border-2 border-[#E2E8F0] text-[#0B132B] hover:border-[#0B132B] px-7 py-4 font-semibold transition"
        >
          <Mail className="h-4 w-4" /> sales@aeromaxenergy.co.nz
        </a>
        <button
          onClick={onRunAgain}
          data-testid="calc-run-again"
          className="inline-flex items-center gap-2 rounded-full border-2 border-[#E2E8F0] text-[#0B132B] hover:border-[#0B132B] px-7 py-4 font-semibold transition"
        >
          <RefreshCw className="h-4 w-4" /> Run Again
        </button>
      </div>
    </div>
  );
}

// ─── Finance comparison panel ────────────────────────────────────────────
function FinancePanel({ finance, monthlySaving }) {
  // Not selected
  if (!finance?.selected) {
    return (
      <Card className="rounded-2xl px-6 py-4 border-[#E2E8F0] bg-[#F8FAFC] flex items-center gap-3">
        <Banknote className="h-4 w-4 text-[#475569] shrink-0" />
        <p className="text-xs text-[#475569]">
          Finance estimate not selected. To see monthly savings vs loan repayment, choose a finance option in Step 4.
        </p>
      </Card>
    );
  }

  // Selected but requires lender confirmation (Kiwibank / Other)
  if (finance.requiresLenderConfirmation) {
    return (
      <Card className="rounded-3xl p-7 sm:p-9 border-[#E2E8F0] bg-gradient-to-br from-[#F8FAFC] to-white">
        <Header label="Monthly Savings vs Loan Repayment" />
        <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0B132B] leading-tight">{finance.label}</h3>
        <div className="mt-4 p-4 rounded-2xl bg-white border border-[#E2E8F0] flex items-start gap-3">
          <Info className="h-4 w-4 text-[#475569] shrink-0 mt-0.5" />
          <p className="text-sm text-[#0B132B]">
            <strong>Requires current rate confirmation.</strong>{" "}
            <span className="text-[#475569]">{finance.note || ""}</span>
          </p>
        </div>
        <p className="mt-4 text-[11px] text-[#475569] leading-relaxed">
          Finance options are subject to lender eligibility, approval criteria, current rates and loan terms. AeroMax can help you understand available options, but finance approval is handled by the lender. AeroMax does not provide financial advice or guarantee loan approval.
        </p>
      </Card>
    );
  }

  const monthlyRepayment = Math.round(finance.monthlyRepayment || 0);
  const netDiff = Math.round(finance.netMonthlyDifference || 0);
  const isPositive = netDiff >= 0;
  const ratePct = finance.rate != null ? `${(finance.rate * 100).toFixed(2)}% p.a.` : "—";

  return (
    <Card className="rounded-3xl p-7 sm:p-9 border-[#E2E8F0] bg-gradient-to-br from-[#F8FAFC] to-white">
      <Header label="Monthly Savings vs Loan Repayment" />
      <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0B132B] leading-tight">{finance.label}</h3>
      <p className="mt-2 text-sm text-[#475569] leading-relaxed max-w-2xl">
        {isPositive
          ? `Your estimated hot water savings may help cover your monthly repayment and leave an estimated net difference of $${netDiff}/month.`
          : "Your estimated hot water savings may offset a meaningful portion of your monthly repayment."}
      </p>
      <div className="mt-5 grid sm:grid-cols-3 gap-3">
        <Stat label="Est. monthly hot water saving" value={`~$${Math.round(monthlySaving)}/mo`} accent />
        <Stat label="Est. monthly loan repayment" value={`~$${monthlyRepayment}/mo`} />
        <Stat label="Estimated net difference" value={`${isPositive ? "+" : ""}$${netDiff}/mo`} accent={isPositive} />
      </div>
      <div className="mt-4 grid sm:grid-cols-3 gap-3 text-[11px] text-[#475569]">
        <Mini label="Rate (special period)" value={ratePct} />
        <Mini label="Term" value={finance.termYears ? `${finance.termYears} years` : "—"} />
        <Mini label="Loan cap" value={finance.cap ? `NZ$${finance.cap.toLocaleString()}` : "—"} />
      </div>
      {finance.note && (
        <p className="mt-3 text-[11px] text-[#475569]"><strong>{finance.label}:</strong> {finance.note}</p>
      )}
      <p className="mt-4 text-[11px] text-[#475569] leading-relaxed">
        Loan repayments are estimates only and depend on lender interest rates, loan term, approval criteria and individual circumstances. AeroMax does not provide financial advice or guarantee loan approval.
      </p>
    </Card>
  );
}

// ─── Assumptions Used (expandable) ───────────────────────────────────────
function AssumptionsUsed({ assumptions, inputs, finance }) {
  const [open, setOpen] = useState(false);
  if (!assumptions) return null;

  const system = inputs?.current_system || "not_sure";
  const rows = [
    { l: "Electricity rate used", v: `${assumptions.electricityRateCentsPerKwh}c / kWh` },
    ["continuous_gas", "gas_storage"].includes(system) && {
      l: "Mains gas daily charge",
      v: `${assumptions.mainsGasDailyChargeCents}c / day`,
    },
    ["continuous_gas", "gas_storage"].includes(system) && {
      l: "Mains gas variable rate",
      v: `${assumptions.mainsGasVariableRateCentsPerKwh}c / kWh`,
    },
    system === "lpg" && { l: "LPG bottle cost", v: `$${assumptions.lpgBottleCost} per 45 kg bottle` },
    system === "lpg" && { l: "LPG energy per bottle", v: `~${assumptions.lpgBottleEnergyKwhEquivalent} kWh` },
    { l: "Installed cost range", v: `$${assumptions.installedCostLow.toLocaleString()}–$${assumptions.installedCostHigh.toLocaleString()}` },
    { l: "Midpoint used for payback / finance", v: `$${assumptions.installedCostMidpoint.toLocaleString()}` },
    { l: "Energy price escalation", v: `${(assumptions.annualEnergyPriceIncrease * 100).toFixed(1)}% / year` },
    { l: "System life", v: `${assumptions.systemLifeYears} years` },
    { l: "NeoPower COP (rated)", v: assumptions.neoPowerCOP.toString() },
    { l: "Conservative COP adjustment", v: `${(assumptions.conservativeRealWorldCOPAdjustment * 100).toFixed(0)}% of rated` },
  ].filter(Boolean);

  if (finance?.selected && !finance.requiresLenderConfirmation) {
    rows.push({ l: "Finance assumed", v: `${finance.label} · ${(finance.rate * 100).toFixed(2)}% p.a. over ${finance.termYears} yrs` });
  }

  return (
    <Card data-testid="calc-assumptions" className="rounded-2xl border-[#E2E8F0] bg-white">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between gap-3 p-6 text-left"
      >
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-[#475569]" />
          <span className="font-heading text-sm font-semibold text-[#0B132B]">Assumptions used</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-[#475569]" /> : <ChevronDown className="h-4 w-4 text-[#475569]" />}
      </button>
      {open && (
        <div className="px-6 pb-6">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center justify-between border-b border-[#E2E8F0] py-2 last:border-0">
                <span className="text-[#475569]">{r.l}</span>
                <span className="font-semibold text-[#0B132B] num-tab text-right">{r.v}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-[#475569] leading-relaxed">
            These assumptions can be updated by AeroMax as pricing and market conditions change.
          </p>
        </div>
      )}
    </Card>
  );
}

// ─── Small UI helpers ────────────────────────────────────────────────────
function Stat({ label, value, accent = false }) {
  return (
    <div className={`rounded-2xl p-4 border ${accent ? "border-[#10B981]/30 bg-[#10B981]/5" : "border-[#E2E8F0] bg-white"}`}>
      <p className="text-[10px] uppercase tracking-widest text-[#475569] font-semibold">{label}</p>
      <p className={`font-heading text-lg sm:text-xl font-bold mt-1 num-tab ${accent ? "text-[#10B981]" : "text-[#0B132B]"}`}>
        {value}
      </p>
    </div>
  );
}

function Tile({ label, value, accent = false }) {
  return (
    <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-3 text-center">
      <p className="text-[10px] uppercase tracking-widest text-[#475569] font-semibold">{label}</p>
      <p className={`font-heading text-2xl font-bold num-tab mt-0.5 ${accent ? "text-[#10B981]" : "text-[#0B132B]"}`}>
        {value}
      </p>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-xl bg-white border border-[#E2E8F0] px-3 py-2">
      <p className="text-[9px] uppercase tracking-widest text-[#475569] font-semibold">{label}</p>
      <p className="text-xs font-semibold text-[#0B132B] mt-0.5 num-tab">{value}</p>
    </div>
  );
}

function Header({ label }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Banknote className="h-4 w-4 text-[#10B981]" />
      <p className="text-xs uppercase tracking-widest font-semibold text-[#475569]">{label}</p>
    </div>
  );
}

// ─── Internal Lead Details (staff mode only) ─────────────────────────────
function InternalLeadPanel({ staffData, lead }) {
  if (!staffData) return null;
  const specialist =
    staffData.specialist === "Other"
      ? staffData.specialist_other || "—"
      : staffData.specialist || "—";
  const installer =
    staffData.installer === "other"
      ? staffData.installer_other || "—"
      : staffData.installer === "none"
      ? "No installation partner selected"
      : staffData.installer === "tbc"
      ? "To be confirmed"
      : staffData.installer || "—";

  const rows = [
    { l: "Energy Saving Specialist", v: specialist },
    { l: "Installation Partner", v: installer },
    { l: "Date of Initiation", v: staffData.date_initiation || "—" },
    { l: "Source", v: staffData.source || "—" },
  ];
  if (staffData.source === "Home Show") {
    rows.push({
      l: "Home Show Name",
      v:
        staffData.home_show === "Other"
          ? staffData.home_show_other || "—"
          : staffData.home_show || "—",
    });
  }
  if (staffData.source === "Referral") {
    if (staffData.referral_code) rows.push({ l: "Referral Code", v: staffData.referral_code });
    if (staffData.referral_name) rows.push({ l: "Referral Notes", v: staffData.referral_name });
  }
  if (staffData.source === "Digital") {
    rows.push({
      l: "Digital Source",
      v:
        staffData.digital_source === "Other"
          ? staffData.digital_source_other || "—"
          : staffData.digital_source || "—",
    });
  }
  if (staffData.source === "Other" && staffData.source_other) {
    rows.push({ l: "Source — other", v: staffData.source_other });
  }
  if (lead?.name) rows.push({ l: "Customer", v: lead.name });
  if (lead?.email) rows.push({ l: "Customer email", v: lead.email });
  if (lead?.phone) rows.push({ l: "Customer phone", v: lead.phone });
  if (lead?.address) rows.push({ l: "Customer address", v: lead.address });

  return (
    <Card
      data-testid="calc-internal-lead-panel"
      className="rounded-2xl border border-amber-300/60 bg-amber-50/60"
    >
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-800 text-[10px] font-bold tracking-widest uppercase">
            Staff Mode · Internal Use
          </span>
          <p className="text-xs uppercase tracking-widest font-semibold text-[#475569]">
            Internal Lead Details
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {rows.map((r, i) => (
            <div
              key={i}
              className="flex items-center justify-between border-b border-amber-200/60 py-2 last:border-0"
            >
              <span className="text-[#475569]">{r.l}</span>
              <span className="font-semibold text-[#0B132B] text-right">{r.v}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-amber-900/80 leading-relaxed">
          Not shown to the customer in public mode. Stored with the lead for follow-up.
        </p>
      </div>
    </Card>
  );
}

